/* Song audio compatibility repair. Keep existing customer URLs and resolve final audio first. */
(function (global) {
  'use strict';
  var controllers = new WeakMap();
  function downloadName(value) {
    var name = String(value || 'song.mp3').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9 ._-]/g, '_').replace(/\s+/g, ' ').trim().slice(0, 120);
    return (name.replace(/\.mp3$/i, '') || 'song') + '.mp3';
  }
  function unique(values) {
    return values.filter(function (value, index, all) { return value && all.indexOf(value) === index; });
  }
  function filename(value) {
    try { return decodeURIComponent(new URL(value).pathname.split('/').pop()).replace(/\.mp3$/i, ''); }
    catch (error) { return ''; }
  }
  function r2URL(base, id) { return base.replace(/\/$/, '') + '/' + encodeURIComponent(id) + '.mp3'; }
  function isR2(value) {
    try { return /^pub-[a-z0-9]+\.r2\.dev$/.test(new URL(value).hostname); }
    catch (error) { return false; }
  }
  function metadata(config, id) {
    if (!config.lookup) return Promise.resolve({});
    var abort = new AbortController();
    var timer = setTimeout(function () { abort.abort(); }, config.lookupTimeout || 8000);
    var url = config.lookup + (config.lookup.indexOf('?') < 0 ? '?' : '&') + 'id=' + encodeURIComponent(id);
    return fetch(url, { signal: abort.signal }).then(function (response) {
      if (!response.ok) throw new Error('lookup unavailable');
      return response.json();
    }).catch(function () { return {}; }).then(function (data) {
      clearTimeout(timer);
      // Accept only the fields the player needs; customer data stays out of the page.
      return { audio_url: data.audio_url || '', timed_lyrics: data.timed_lyrics || null,
        name: data.name || null, occasion: data.occasion || null };
    });
  }
  function prepare(config) {
    var id = String(config.id || '').replace(/\.mp3$/i, '');
    if (!id) return Promise.reject(new Error('missing song identifier'));
    return metadata(config, id).then(function (data) {
      var legacy = (config.shopifyBases || []).map(function (base) { return r2URL(base, id); });
      var direct = config.r2Base ? [r2URL(config.r2Base, id)] : [];
      var stored = isR2(data.audio_url) ? [data.audio_url] : [];
      var old = data.audio_url && !isR2(data.audio_url) ? [data.audio_url] : [];
      // Old temporary-file links use the same UUID as their permanent R2 copy.
      var oldId = filename(data.audio_url);
      if (config.r2Base && oldId && /^[a-f0-9-]{36}$/i.test(oldId)) direct.push(r2URL(config.r2Base, oldId));
      var temporary = config.temporaryBase ? [r2URL(config.temporaryBase, id)] : [];
      var sources = unique(config.preferLegacyForNumeric && /^\d+$/.test(id) && !stored.length
        ? legacy.concat(direct, old, temporary) : stored.concat(direct, legacy, old, temporary));
      if (!sources.length) throw new Error('no song source');
      return { sources: sources, url: sources[0], data: data, config: config };
    });
  }
  function bind(media, song, options) {
    options = options || {};
    var config = song.config || {}, labels = config.labels || {};
    var index = -1, timer = null, wanted = !media.paused, switching = false, exhausted = false;
    var status = document.createElement('div');
    status.setAttribute('role', 'status');
    status.style.cssText = 'font:14px/1.5 system-ui,sans-serif;text-align:center;margin:12px auto;max-width:480px';
    var message = document.createElement('span');
    var retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = labels.retry || 'Retry';
    retry.style.cssText = 'display:none;margin:8px;padding:8px 14px;border:1px solid currentColor;border-radius:8px;background:transparent;color:inherit;cursor:pointer';
    status.appendChild(message); status.appendChild(retry);
    (options.statusHost || media.parentNode).appendChild(status);
    function announce(text, failed) {
      status.hidden = !text; message.textContent = text || '';
      retry.style.display = failed ? 'inline-block' : 'none';
    }
    function clear() { if (timer !== null) clearTimeout(timer); timer = null; }
    function failure() {
      clear(); exhausted = true; wanted = false; switching = false;
      announce(labels.unavailable || 'The audio could not be loaded. Please retry.', true);
      if (options.onUnavailable) options.onUnavailable();
    }
    function next() {
      if (exhausted) return;
      clear(); index++;
      if (index >= song.sources.length) { failure(); return; }
      switching = true;
      media.pause();
      media.src = song.sources[index];
      song.url = song.sources[index];
      if (options.onSource) options.onSource(song.url);
      announce(labels.loading || 'Loading your song…', false);
      media.load();
      var attempt = index;
      timer = setTimeout(function () {
        if (!exhausted && index === attempt && media.readyState < 1) next();
      }, config.mediaTimeout || 20000);
    }
    function rejected(error) {
      if (error && error.name === 'AbortError') return;
      if (error && error.name === 'NotAllowedError') {
        wanted = false;
        announce(labels.tap || 'Press play to listen to your song.', false);
      } else if (!exhausted && media.error) {
        // The media error event owns fallback; do not skip another source here.
        announce(labels.loading || 'Loading your song…', false);
      } else if (!exhausted) {
        announce(labels.tap || 'Press play to listen to your song.', false);
      }
    }
    function play() {
      wanted = true;
      var promise = media.play();
      if (!promise || !promise.catch) return Promise.resolve();
      return promise.catch(function (error) { rejected(error); throw error; });
    }
    media.preload = 'metadata';
    media.setAttribute('playsinline', '');
    media.addEventListener('loadedmetadata', function () {
      clear(); switching = false; exhausted = false; announce('', false);
      if (options.onReady) options.onReady();
      if (wanted) play().catch(function () {});
    });
    media.addEventListener('error', next);
    media.addEventListener('play', function () { wanted = true; announce('', false); });
    media.addEventListener('pause', function () { if (!switching) wanted = false; });
    media.addEventListener('ended', function () { wanted = false; });
    media.addEventListener('waiting', function () {
      if (!wanted || exhausted) return;
      clear();
      var attempt = index;
      timer = setTimeout(function () {
        if (!exhausted && wanted && index === attempt && media.readyState < 3) next();
      }, config.mediaTimeout || 20000);
    });
    media.addEventListener('playing', function () { clear(); announce('', false); });
    retry.addEventListener('click', function () { exhausted = false; index = -1; next(); });
    controllers.set(media, { play: play });
    next();
    return song;
  }
  global.SongAudio = { prepare: prepare, bind: bind, downloadName: downloadName, play: function (media) {
    var controller = controllers.get(media);
    return controller ? controller.play() : media.play();
  } };
})(window);
